import { formatMoney } from '@/lib/format';
import type { CashSummary } from '@/lib/cashSession';

interface SummarySectionProps {
  summary: CashSummary;
  currencySymbol: string;
}

function StatTile({
  icon,
  label,
  value,
  tone = 'bg-background-200/70',
  valueClassName = 'text-foreground-950',
}: {
  icon: string;
  label: string;
  value: string;
  tone?: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
      <div className="flex items-center gap-2 text-foreground-600">
        <span className={`w-8 h-8 rounded-md flex items-center justify-center ${tone}`}>
          <i className={`${icon} text-base`} />
        </span>
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className={`mt-2 font-heading text-xl font-bold ${valueClassName}`}>{value}</p>
    </div>
  );
}

export default function SummarySection({ summary, currencySymbol }: SummarySectionProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <h2 className="font-heading text-lg font-semibold text-foreground-950">Resumen del cuadre</h2>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon="ri-receipt-line" label="Ventas" value={String(summary.salesCount)} />
        <StatTile
          icon="ri-shopping-bag-3-line"
          label="Subtotal"
          value={formatMoney(summary.subtotal, currencySymbol)}
        />
        <StatTile
          icon="ri-percent-line"
          label="Descuentos"
          value={`-${formatMoney(summary.discount, currencySymbol)}`}
        />
        <StatTile
          icon="ri-percent-fill"
          label="Impuestos"
          value={formatMoney(summary.tax, currencySymbol)}
        />
      </div>

      <div className="mt-5 space-y-1.5 text-sm border-t border-background-200 pt-4">
        {summary.returnsTotal > 0 && (
          <div className="flex justify-between">
            <span className="text-foreground-600">Devoluciones</span>
            <span className="font-medium text-accent-700">
              -{formatMoney(summary.returnsTotal, currencySymbol)}
            </span>
          </div>
        )}
        {summary.returnsTotal > 0 && (
          <div className="flex justify-between">
            <span className="text-foreground-600">Neto</span>
            <span className="font-medium text-foreground-900">
              {formatMoney(summary.net, currencySymbol)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between pt-1">
          <span className="font-heading font-semibold text-foreground-950">Total vendido</span>
          <span className="font-heading text-2xl font-bold text-primary-700">
            {formatMoney(summary.total, currencySymbol)}
          </span>
        </div>
      </div>

      {/* Rentabilidad real de las ventas del período */}
      <div className="mt-5 border-t border-background-200 pt-4">
        <div className="flex items-center gap-2 text-foreground-700">
          <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
            <i className="ri-line-chart-line" />
          </span>
          <span className="text-sm font-semibold text-foreground-950">
            Margen de ganancia de las ventas
          </span>
        </div>

        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile
            icon="ri-money-dollar-circle-line"
            label="Ingresos netos"
            value={formatMoney(summary.revenue, currencySymbol)}
            tone="bg-primary-100 text-primary-700"
          />
          <StatTile
            icon="ri-shopping-bag-3-line"
            label="Costo de mercancía"
            value={formatMoney(summary.cost, currencySymbol)}
            tone="bg-background-200 text-foreground-700"
          />
          <StatTile
            icon="ri-hand-coin-line"
            label="Ganancia"
            value={formatMoney(summary.profit, currencySymbol)}
            tone="bg-accent-100 text-accent-700"
            valueClassName={summary.profit >= 0 ? 'text-accent-800' : 'text-primary-800'}
          />
          <StatTile
            icon="ri-percent-line"
            label="Margen promedio"
            value={`${summary.margin.toFixed(1)}%`}
            tone="bg-secondary-100 text-secondary-900"
          />
        </div>
      </div>
    </div>
  );
}